import { Group, Stack } from '@mantine/core';
import { useApiGetDbtModelNames } from '@repo/api-codegen';
import { Button, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import SearchBox from '../../../SearchBox/SearchBox';
import { useAddMonitorStoreContext } from '../hooks';

interface ModelNameSelectorProps {
	title?: string;
}

function ModelNameSelector({ title }: ModelNameSelectorProps) {
	const store = useAddMonitorStoreContext();
	const [searchTerm, setSearchTerm] = useState('');

	// Fetch dbt model names from the API
	const {
		data: availableModels = [],
		isLoading,
		error,
	} = useApiGetDbtModelNames(
		{
			queryParams: {
				integration_id: store.integration || '',
			},
		},
		{
			enabled: !!store.integration,
		}
	);

	const handleSearch = useCallback((term: string) => {
		setSearchTerm(term);
	}, []);

	const handleModelSelect = useCallback(
		(modelName: string) => {
			store.setMetricConfigFilter('model_name', modelName);
		},
		[store]
	);

	const filteredModels = availableModels.filter((model) =>
		model.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const selectedModel = store.metricConfig.filter?.model_name;

	return (
		<Stack spacing="sm" pb="xs">
			{title && (
				<Text weight="semibold" size="sm">
					{title}
				</Text>
			)}
			<Group sx={{ flexGrow: 1 }}>
				<SearchBox
					variant="tertiary"
					placeholder="Search dbt models"
					onSearch={handleSearch}
				/>
			</Group>
			<Stack spacing="xs" style={{ maxHeight: 200, overflowY: 'auto' }}>
				{isLoading && (
					<Text size="sm" color="text/secondary/default" ta="center" py="md">
						Loading models...
					</Text>
				)}
				{error && (
					<Text size="sm" color="text/critical/default" ta="center" py="md">
						Error loading models. Please try again.
					</Text>
				)}
				{!isLoading && !error && filteredModels.length === 0 && (
					<Text size="sm" color="text/secondary/default" ta="center" py="md">
						{availableModels.length === 0
							? 'No dbt models found. Please run analytics extraction first.'
							: 'No models match your search.'}
					</Text>
				)}
				{filteredModels.map((modelName) => (
					<Button
						key={modelName}
						variant={selectedModel === modelName ? 'primary' : 'tertiary'}
						size="sm"
						onClick={() => handleModelSelect(modelName)}
						style={{ width: '100%', justifyContent: 'left' }}
					>
						{modelName}
					</Button>
				))}
			</Stack>
			{selectedModel && (
				<Text size="sm" color="text/secondary/default">
					Selected: {selectedModel}
				</Text>
			)}
		</Stack>
	);
}

const ObservedModelNameSelector = observer(ModelNameSelector);
export default ObservedModelNameSelector;
